import { useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { Avatar } from '@/components/tree/Avatar'
import { albumPhotoUrl, peopleIn, type Album } from '@/lib/albums'
import { SIZE_KEY, SIZES, showValue, type Show, type WallPhoto } from '@/lib/modern'
import { remembered } from '@/lib/remember'
import type { Selection } from '@/lib/selection'
import { PhotoPost } from './PhotoPost'
import { useOpenPhoto } from './useOpenPhoto'
import { Wall, type WallHandle } from './Wall'

// The top of an album in the Modern style of Photos: its cover in a ring like a profile, its name and whose album it
// is, the keeper's few words, and the people in it as rings that open their photos across the circle.
export function ModernAlbumTop({
  album,
  when,
  forWhom,
  about,
}: {
  album: Album
  when: string | null
  forWhom: string
  about: ReactNode
}) {
  const cover = album.photos.find((p) => p.id === album.coverPhotoId) ?? album.photos[0]
  const people = peopleIn(album.photos)
  const meta = [album.by && `From the album of ${album.by}`, when, forWhom && `For ${forWhom}`].filter(
    (x): x is string => !!x,
  )
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4 sm:gap-6">
        {cover && (
          <span className="mp-ring-o mp-cover shrink-0" aria-hidden="true">
            <span>
              <img src={albumPhotoUrl(cover.ref, 'thumb')} alt="" className="size-full object-cover" decoding="async" />
            </span>
          </span>
        )}
        <div className="flex min-w-0 flex-col gap-1.5">
          {album.collection && <p className="eyebrow">{album.collection.title}</p>}
          <h1 className="text-title font-semibold tracking-tight text-balance sm:text-display">{album.title}</h1>
          {meta.length > 0 && <p className="text-sub text-pretty text-muted-foreground">{meta.join(' · ')}</p>}
        </div>
      </div>
      {album.description && <div className="flex">{about}</div>}
      {people.length > 0 && (
        <ul className="-mx-4 scroll-row gap-2 px-4 py-1 nav:-mx-2 nav:px-2" aria-label="People in this album">
          {people.map((p) => (
            <li key={p.id} className="shrink-0 snap-start">
              <Link
                to={`/circle/${album.familyId}/photos?${new URLSearchParams({ show: `person:${p.id}` })}`}
                className="mp-ring"
                aria-label={`Photos of ${p.name}`}
              >
                <span className="mp-ring-o">
                  <span>
                    <Avatar person={p} className="size-full text-2xl shadow-none" />
                  </span>
                </span>
                <span className="w-full truncate text-xs text-muted-foreground">{p.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// An album's photos in the Modern style: every one whole on the wall, in the album's order, each opening as a post
// with the photos from the same day under it. A tag tapped on a post opens Photos narrowed to it.
export function ModernAlbumPhotos({
  album,
  selection,
  moments,
  feed,
}: {
  album: Album
  selection: Selection
  moments: boolean
  feed?: boolean
}) {
  const photos: WallPhoto[] = useMemo(
    () => album.photos.map((p) => ({ ...p, album: { id: album.id, title: album.title } })),
    [album.photos, album.id, album.title],
  )
  return (
    <PhotoWall
      photos={photos}
      familyId={album.familyId}
      label="Photos in this album"
      moments={moments}
      selection={selection}
      here={{ kind: 'album', id: album.id }}
      feed={feed}
    />
  )
}

// A wall of whole photos anywhere outside Photos (an album, a person's page), each opening as a post. A tag tapped on
// a post opens Photos narrowed to it, unless it's the tag for this very page (here), which just puts the post away.
// Two walls on one page keep their open photo under different names in the address (param).
export function PhotoWall({
  photos,
  familyId,
  label,
  moments,
  selection,
  here,
  param,
  feed,
}: {
  photos: WallPhoto[]
  familyId: string
  label: string
  moments: boolean
  selection?: Selection
  here?: Show
  param?: string
  feed?: boolean
}) {
  const navigate = useNavigate()
  const wall = useRef<WallHandle>(null)
  // The size picked under Filter on Photos carries to every wall.
  const [smaller] = useState(() => remembered(SIZE_KEY, SIZES, 'bigger') === 'smaller')
  const { openId, open, close, go, swap } = useOpenPhoto(wall, param)
  const show = (next: Show) => {
    if (here && showValue(next) === showValue(here)) close()
    else navigate(`/circle/${familyId}/photos?${new URLSearchParams({ show: showValue(next) })}`)
  }
  return (
    <>
      <Wall
        ref={wall}
        photos={photos}
        label={label}
        selected={selection?.selected}
        onToggle={selection?.selecting ? selection.toggle : undefined}
        onOpen={open}
        smaller={smaller}
        feed={feed}
      />
      {openId && (
        <PhotoPost
          id={openId}
          known={photos.find((p) => p.id === openId)}
          list={photos}
          familyId={familyId}
          moments={moments}
          onClose={close}
          onGo={go}
          onSwap={swap}
          onShow={show}
        />
      )}
    </>
  )
}
